import { useMemo, useState } from 'react'

import moment from 'moment'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { mapCreditCardBillFormSchemaToPayload } from '@webapp/modules/accounts-payable/data-access/mappers/mapCreditCardBillFormSchemaToPayload'
import { type CreditCardAccount } from '@webapp/modules/accounts-payable/entities/CreditCardAccount'
import { type ReconcileSession } from '@webapp/modules/accounts-payable/entities/ReconcileSession'
import { type VendorPaymentMethod } from '@webapp/modules/vendor/entities/VendorPaymentMethod'
import messages from './messages'
import {
  useMutateSubmitCreditCardBill,
  useMutateUploadInvoiceAttachment,
  useQueryCreditCardBillFormData,
  useQuerySessionApproverChoices,
} from '../../models'
import {
  type ApprovalChainOption,
  type ApproverOption,
  type CreditCardBillFormSchema,
  type PaymentMethodOption,
  type UploadedInvoice,
} from '../../views/CreditCardBillForm'

export interface IUseCreateCreditCardBillFormPresenterParams {
  account: CreditCardAccount
  session: ReconcileSession
  additionalPaymentMethods: VendorPaymentMethod[]
  onSubmitted: () => void
}

export const useCreateCreditCardBillFormPresenter = ({
  account,
  session,
  additionalPaymentMethods,
  onSubmitted,
}: IUseCreateCreditCardBillFormPresenterParams) => {
  const intl = useProcurifyIntl()

  // Read-only invoice date — the due-date presets are relative to it (BHV-045).
  const invoiceDate = useMemo(() => new Date(), [])

  const [approvalChainId, setApprovalChainId] = useState<Nullable<number>>(null)

  const { paymentMethods, approvalChains, skipApproval } =
    useQueryCreditCardBillFormData({
      vendorId: account.vendor.id,
      cardCurrencyId: account.currency.id,
      onError: (error) => {
        toast.error({
          title: intl.formatMessage(messages.errorTitle),
          children:
            getMessageFromError(error) ??
            intl.formatMessage(messages.genericErrorMessage),
        })
      },
    })

  const { choices, isFetching: isApproverLoading } =
    useQuerySessionApproverChoices({
      sessionId: session.id,
      approvalChainId,
    })

  const { submitBill, isSubmitting } = useMutateSubmitCreditCardBill()
  const { uploadInvoice } = useMutateUploadInvoiceAttachment()

  // Card-currency payment methods + any created in this session, deduped.
  const paymentMethodOptions = useMemo<PaymentMethodOption[]>(() => {
    const byId = new Map<number, VendorPaymentMethod>()
    ;[...paymentMethods, ...additionalPaymentMethods]
      .filter((method) => method.matchesCurrency(account.currency.id))
      .forEach((method) => byId.set(method.id, method))

    return Array.from(byId.values()).map((method) => ({
      value: method.id,
      label: method.name,
    }))
  }, [paymentMethods, additionalPaymentMethods, account.currency.id])

  const approvalChainOptions = useMemo<ApprovalChainOption[]>(
    () =>
      approvalChains.map((chain) => ({ value: chain.id, label: chain.name })),
    [approvalChains]
  )

  const approverOptions = useMemo<ApproverOption[]>(
    () =>
      choices.map((choice) => ({
        value: choice.id,
        label: choice.userFullName,
        userId: choice.user.id,
      })),
    [choices]
  )

  const onUploadInvoice = async (
    file: File
  ): Promise<Nullable<UploadedInvoice>> => {
    try {
      return await uploadInvoice(file)
    } catch (error) {
      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.uploadErrorMessage),
      })

      return null
    }
  }

  const onSubmit = async (values: CreditCardBillFormSchema) => {
    try {
      await submitBill({
        sessionId: session.id,
        accountId: account.id,
        payload: mapCreditCardBillFormSchemaToPayload({
          notes: values.notes,
          paymentMethod: values.paymentMethod,
          dueDate: values.dueDate,
          invoiceDate: moment(invoiceDate),
          approvalChain: values.approvalGroup,
          approver: values.approver
            ? { value: values.approver.value, userId: values.approver.userId }
            : null,
          invoiceAttachmentUuids: values.invoiceAttachments.map(
            (invoice) => invoice.uuid
          ),
        }),
      })

      toast.success({
        title: intl.formatMessage(messages.successTitle),
        children: intl.formatMessage(messages.successMessage),
      })

      onSubmitted()
    } catch (error) {
      toast.error({
        title: intl.formatMessage(messages.errorTitle),
        children:
          getMessageFromError(error) ??
          intl.formatMessage(messages.genericErrorMessage),
      })
    }
  }

  return {
    state: {
      invoiceDate,
      paymentMethodOptions,
      approvalChainOptions,
      approverOptions,
      skipApproval,
      isApproverLoading,
      isSubmitting,
    },
    methods: {
      onApprovalChainChange: setApprovalChainId,
      onUploadInvoice,
      onSubmit,
    },
  }
}
